import React, { lazy, Suspense } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { useSlideNavigation } from './hooks/useSlideNavigation';
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import HeroSlide from './components/slides/HeroSlide';
import ThesisSlide from './components/slides/ThesisSlide';

const TimelineSlide = lazy(() => import('./components/slides/TimelineSlide'));
const TurningPointsSlide = lazy(() => import('./components/slides/TurningPointsSlide'));
const HxaroSlide = lazy(() => import('./components/slides/HxaroSlide'));
const GiftEconomiesSlide = lazy(() => import('./components/slides/GiftEconomiesSlide'));
const ComparisonSlide = lazy(() => import('./components/slides/ComparisonSlide'));
const WhySlide = lazy(() => import('./components/slides/WhySlide'));
const CollaborationPowerSlide = lazy(() => import('./components/slides/CollaborationPowerSlide'));
const DataDeepDiveSlide = lazy(() => import('./components/slides/DataDeepDiveSlide'));
const QuizSlide = lazy(() => import('./components/slides/QuizSlide'));
const SimulatorSlide = lazy(() => import('./components/slides/SimulatorSlide'));
const ModernSlide = lazy(() => import('./components/slides/ModernSlide'));
const FutureSlide = lazy(() => import('./components/slides/FutureSlide'));
const SourcesSlide = lazy(() => import('./components/slides/SourcesSlide'));
const CTASlide = lazy(() => import('./components/slides/CTASlide'));

const SlideFallback = () => {
  const { locale } = useLanguage();
  const loadingByLocale = {
    en: 'Loading...',
    de: 'Laedt...',
    es: 'Cargando...',
  };

  return (
    <section className="theme-shell min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#9cb4e7] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm" style={{ color: 'var(--hx-muted)' }}>{loadingByLocale[locale] || loadingByLocale.en}</p>
      </div>
    </section>
  );
};

const TOTAL_SLIDES = 16;

function AppContent() {
  const { currentSlide, goTo, next, registerSlide, total } = useSlideNavigation(TOTAL_SLIDES);

  return (
    <div className="theme-shell">
      <Navigation currentSlide={currentSlide} goTo={goTo} total={total} />
      <main>
        <ErrorBoundary>
          <HeroSlide refCallback={registerSlide(0)} onNext={next} />
          <ThesisSlide refCallback={registerSlide(1)} />
          <Suspense fallback={<SlideFallback />}>
            <TimelineSlide refCallback={registerSlide(2)} />
            <TurningPointsSlide refCallback={registerSlide(3)} />
            <HxaroSlide refCallback={registerSlide(4)} />
            <GiftEconomiesSlide refCallback={registerSlide(5)} />
            <ComparisonSlide refCallback={registerSlide(6)} />
            <WhySlide refCallback={registerSlide(7)} />
            <CollaborationPowerSlide refCallback={registerSlide(8)} />
            <DataDeepDiveSlide refCallback={registerSlide(9)} />
            <QuizSlide refCallback={registerSlide(10)} />
            <SimulatorSlide refCallback={registerSlide(11)} />
            <ModernSlide refCallback={registerSlide(12)} />
            <FutureSlide refCallback={registerSlide(13)} />
            <SourcesSlide refCallback={registerSlide(14)} />
            <CTASlide refCallback={registerSlide(15)} goTo={goTo} />
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App
